/**
 * DossierHistoryDrawer — collapsible side drawer showing "who did what when" on a dossier.
 * Fetches /api/dossiers/{id}/history and renders a reverse-chronological timeline.
 */
import React, { useCallback, useEffect, useState } from "react";
import { History, X, Loader2, User, FileEdit, CheckCircle2, Hammer, Phone } from "lucide-react";
import { api } from "../lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "à l'instant";
  const m = Math.floor(sec / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const day = Math.floor(h / 24);
  if (day < 7) return `il y a ${day} j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
}

function ActionIcon({ action }) {
  if (action === "workflow_step" || action === "workflow_phase") return <Hammer className="w-3.5 h-3.5 text-amber-700" />;
  if (action === "call_transcribed") return <Phone className="w-3.5 h-3.5 text-norea-primary" />;
  if (action === "tab_status") return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />;
  if (action === "attachment_add" || action === "attachment_delete") return <FileEdit className="w-3.5 h-3.5 text-norea-primary" />;
  return <FileEdit className="w-3.5 h-3.5 text-norea-textMuted" />;
}

function formatValue(v) {
  if (v === null || v === undefined || v === "") return <em className="text-norea-textMuted">vide</em>;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function HistoryItem({ h }) {
  const author = (h.user_email || "system").split("@")[0];
  return (
    <li className="relative pl-6 pb-4 border-l border-norea-border last:border-transparent" data-testid={`history-item-${h.id}`}>
      <span className="absolute -left-[7px] top-0.5 w-3 h-3 rounded-full bg-white border border-norea-border flex items-center justify-center">
        <ActionIcon action={h.action} />
      </span>
      <div className="text-xs text-norea-text">
        <strong className="text-norea-primary">@{author}</strong>
        <span className="text-norea-textMuted"> · {h.scope} · </span>
        <span className="font-medium">{h.label}</span>
      </div>
      {(h.before !== undefined || h.after !== undefined) && (
        <div className="text-[11px] text-norea-textMuted mt-0.5 line-clamp-2">
          {formatValue(h.before)} → <strong className="text-norea-text">{formatValue(h.after)}</strong>
        </div>
      )}
      <div className="text-[10px] text-norea-textMuted mt-0.5">{timeAgo(h.created_at)}</div>
    </li>
  );
}

export default function DossierHistoryDrawer({ dossierId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dossiers/${dossierId}/history`);
      setItems(r.data.items || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [dossierId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Lightweight polling while open
  useEffect(() => {
    if (!open) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [open, load]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed right-0 top-[200px] z-20 bg-white border border-norea-border border-r-0 rounded-l-md px-2 py-3 shadow hover:bg-norea-surfaceHover transition flex flex-col items-center gap-1"
        title="Historique du dossier"
        data-testid="btn-open-history"
      >
        <History className="w-4 h-4 text-norea-primary" />
        <span className="text-[10px] uppercase font-semibold tracking-wider text-norea-textMuted" style={{ writingMode: "vertical-rl" }}>
          Historique
        </span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setOpen(false)} />
          <aside
            className="fixed right-0 top-0 bottom-0 w-[400px] max-w-full z-50 bg-white border-l border-norea-border overflow-y-auto shadow-2xl"
            data-testid="history-drawer"
          >
            <div className="sticky top-0 bg-white border-b border-norea-border px-4 py-3 flex items-center justify-between">
              <div>
                <h3 className="font-heading text-sm font-semibold text-norea-text">Historique du dossier</h3>
                <div className="text-[11px] text-norea-textMuted">
                  {items.length} action(s) · rafraîchi toutes les 15 s
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-norea-surfaceHover rounded" data-testid="btn-close-history">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4">
              {loading && items.length === 0 ? (
                <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
              ) : items.length === 0 ? (
                <div className="p-8 text-center text-xs text-norea-textMuted italic">
                  <User className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Aucune action pour l'instant. Dès que tu modifies un champ, un statut ou une étape, ça apparaîtra ici.
                </div>
              ) : (
                <ul className="space-y-0">
                  {items.map((h) => <HistoryItem key={h.id} h={h} />)}
                </ul>
              )}
            </div>
          </aside>
        </>
      )}
    </>
  );
}
